// The navigational lifecycle's REGISTRY: the record shapes one connection's
// entry is made of, the module-level map that owns them, the liveness check
// every pending wait re-runs, and the teardown that empties an entry.
//
// Split out of `navigationalLifecycle.ts` (which keeps the architectural
// overview of the whole area) so the state sits apart from the two things that
// DRIVE it — the case-policy gate in `navLifecycleDatabaseResolution.ts` and
// the build in `navLifecycleBuild.ts`. Both of those import this module and
// this module imports neither, so the area has no cycle.
//
// The lifecycle is module state (one map across the renderer); the connection
// store is itself a singleton, so collocating the lifecycle map with it is
// safe.

import type { DatabaseEngineId } from '@/core/engines';

import type { IdentifierIndex } from '../types';

import type { IdentifierIndexOptions } from '../index/identifierIndex';
import type { IndexInvalidatorRegistration } from '../index/indexInvalidator';
import { disposePeekReferenceModelsForConnection } from '../find-references/peekReferenceModels';
import { forgetCasePolicy } from '../casePolicyState';

import { isNavIndexUnavailable, navIndexUnavailable } from './navIndexUnavailable';
import type { SnapshotWaitTarget } from './navIndexSchedule';

export type LifecycleDeps = {
  readonly createIdentifierIndex: (connectionId: string, options: IdentifierIndexOptions) => IdentifierIndex;
  readonly buildIndexForConnection: (
    options: {
      connectionId: string;
      database: string;
      engineId: DatabaseEngineId;
      /** Flipped by teardown — the builder checks it between parse slices. */
      shouldAbort?: () => boolean;
    },
    index: IdentifierIndex,
  ) => Promise<void>;
  readonly registerDdlInvalidator: (reg: IndexInvalidatorRegistration) => () => void;
  /** Gate (a): the engine's schema snapshot is done. */
  readonly waitForSchemaSnapshot: (target: SnapshotWaitTarget) => Promise<void>;
  /** Gate (b): an idle moment. */
  readonly waitForIdleMoment: () => Promise<void>;
  /** Gate (c): learn the CONNECTION's case policy before ANY key exists —
   *  resolves `true` when it is known. Engines with nothing to probe answer
   *  `true` without a query; see `navCasePolicy.ts`. */
  readonly ensureCasePolicy: (
    connectionId: string,
    engineId: DatabaseEngineId,
    database: string,
  ) => Promise<boolean>;
  /** Gate (d): learn THIS DATABASE's case policy — on SQL Server its own
   *  collation, which is what object and schema names compare in. Runs after
   *  (c), whose answer decides the identity this is filed under. */
  readonly ensureDatabaseCasePolicy: (
    connectionId: string,
    engineId: DatabaseEngineId,
    database: string,
  ) => Promise<boolean>;
  /** Learnt alongside (c), for a connection configured with NO database: the
   *  name the login's default database actually has. `undefined` when it cannot
   *  be learnt. See `rememberDefaultDatabase`
   *  (`navLifecycleDatabaseResolution.ts`) for why it is worth a row. */
  readonly resolveDefaultDatabase: (
    connectionId: string,
    engineId: DatabaseEngineId,
  ) => Promise<string | undefined>;
};

/** One database identity's index on a connection, plus the handles to drive
 *  its build. */
export type DatabaseIndexEntry = {
  readonly index: IdentifierIndex;
  /** The database the catalog queries RUN IN — the database this entry is
   *  ABOUT on the engines where the name IS the identity, and the CONNECTION's
   *  own wherever one index spans the whole connection and the name therefore
   *  only routes the query (`routingDatabaseFor`, which holds the argument:
   *  routing with the first requester's name let one early bad request poison
   *  the connection's only entry). Also what the DDL registration reads as the
   *  connection's implicit schema (`index/indexInvalidator.ts`). */
  readonly database: string;
  /** The CURRENT build's readiness. Replaced when a FAILED build is retried
   *  (`runBuild`); never replaced for a cancelled one, whose entry teardown has
   *  already removed. */
  ready: Promise<void>;
  /** True while the last build REJECTED: the index keeps what the builder had
   *  recorded (marked partial) and the next request EMPTIES it and rebuilds
   *  into the same object (`runBuild`). */
  buildFailed: boolean;
  /** Flipped by {@link DatabaseIndexEntry.cancel}; the builder polls it. */
  cancelled: boolean;
  readonly disposeInvalidator: () => void;
  /** Cancel a pending or in-progress build. Idempotent. */
  readonly cancel: () => void;
};

export type ConnectionEntry = {
  readonly engineId: DatabaseEngineId;
  /** The connection dialog's database — `''` when none was configured, in
   *  which case the catalog queries run in the login's default database. */
  readonly defaultDatabase: string;
  /** That login default's actual NAME, learnt once at the case-policy gate when
   *  `defaultDatabase` is `''` (see `rememberDefaultDatabase`,
   *  `navLifecycleDatabaseResolution.ts`).
   *  `undefined` means "not applicable, or the probe failed" — `''` then keeps
   *  routing to the login default exactly as before. */
  resolvedDefaultDatabase?: string;
  readonly deps: LifecycleDeps;
  /** Keyed by {@link databaseIdentityKey}. */
  readonly databases: Map<string, DatabaseIndexEntry>;
  /** The connection's in-flight case-policy probe — one for every database,
   *  cleared when it fails so the next request asks again (see the header). */
  casePolicy?: Promise<void>;
  /** The in-flight per-DATABASE case-policy probe, keyed by database identity.
   *  Same rule as `casePolicy`: a failed attempt is deleted so the next
   *  request for that database asks again. */
  readonly databaseCasePolicy: Map<string, Promise<void>>;
};

export const liveLifecycle = new Map<string, ConnectionEntry>();

/** Distinguishes two ways a wait's `entry` can stop being the live one:
 *  - no entry at all for `connectionId` (plain teardown, no reconnect) →
 *    `'no-lifecycle'` — there is genuinely nothing to search.
 *  - a DIFFERENT entry now lives under the same id (the connection was torn
 *    down and re-created — reconnect — while this wait was pending) →
 *    `'cancelled'`. The replacement lifecycle may not have learnt its own
 *    policy yet (or may still be mid-build), so this wait must NOT be read as
 *    "no index" — that would let a caller fall through to the replacement's
 *    absent or unfinished index and treat it as a completed (empty) search. */
export function assertStillLive(connectionId: string, entry: ConnectionEntry): void {
  const current = liveLifecycle.get(connectionId);
  if (current === entry) return;
  if (current === undefined) {
    throw navIndexUnavailable('no-lifecycle', `no nav index for connection ${connectionId}`);
  }
  throw navIndexUnavailable(
    'cancelled',
    `nav index lifecycle for connection ${connectionId} was replaced (reconnect during pending wait)`,
  );
}

/** The two typed reasons that mean "this connection stopped being the one this
 *  schedule was started for" — `assertStillLive`'s pair. */
export function isOrdinaryDisconnect(err: unknown): boolean {
  return isNavIndexUnavailable(err) && (err.reason === 'no-lifecycle' || err.reason === 'cancelled');
}

export function tearDownConnection(connectionId: string): void {
  const entry = liveLifecycle.get(connectionId);
  if (!entry) return;
  for (const dbEntry of entry.databases.values()) {
    // Cancel FIRST: a build still paging bodies for a connection that just
    // went away would keep spending IPC on a catalog nobody will read, and
    // would record into an index we are about to clear.
    dbEntry.cancel();
    dbEntry.disposeInvalidator();
    dbEntry.index.clear();
  }
  entry.databases.clear();
  entry.databaseCasePolicy.clear();
  // Synthesized peek models mirror this connection's catalog — a disconnect
  // makes them stale, so they die with the index rather than lingering in
  // Monaco's model registry across a reconnect.
  disposePeekReferenceModelsForConnection(connectionId);
  // Bumps the connection's case-policy generation too, so a probe still in
  // flight cannot publish an answer about a server this id no longer points at
  // (`casePolicyState.ts`).
  forgetCasePolicy(connectionId);
  liveLifecycle.delete(connectionId);
}
